import { adminService } from "@/api/admin/admin.service";
import type {
  TAdminSection,
  TCreateSectionRequest,
  TUpdateSectionRequest,
} from "@/api/admin/admin.types";
import {
  DataGrid,
  type TColumnDef,
  type TDataGridQuery,
} from "@/components/data-grid";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/utils";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";

const PAGE_SIZE = 20;

type TRoleFilter = "" | "FUNDRAISER" | "INVESTOR";

type TEditorState = {
  open: boolean;
  section: TAdminSection | null;
  name: string;
  description: string;
  roleType: TRoleFilter;
  order: string;
  isActive: boolean;
};

const EMPTY_EDITOR: TEditorState = {
  open: false,
  section: null,
  name: "",
  description: "",
  roleType: "",
  order: "0",
  isActive: true,
};

export function SectionsPage() {
  const { toast } = useToast();
  const [roleFilter, setRoleFilter] = useState<TRoleFilter>("");
  const [reloadTick, setReloadTick] = useState(0);

  const [editor, setEditor] = useState<TEditorState>(EMPTY_EDITOR);
  const [isSaving, setIsSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<TAdminSection | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const reload = () => setReloadTick((t) => t + 1);

  // Client mode: fetch the full (role-filtered) list once; search/sort/paginate in-memory.
  // The role filter is a real query param (adminService.getSections) applied via deps.
  const fetcher = async (query: TDataGridQuery) => {
    const { data, error: err } = await adminService.getSections(roleFilter || undefined, {
      signal: query.signal,
    });
    if (err) throw err;
    return data;
  };

  const openCreate = () => setEditor({ ...EMPTY_EDITOR, open: true });

  const openEdit = (section: TAdminSection) =>
    setEditor({
      open: true,
      section,
      name: section.name ?? "",
      description: section.description ?? "",
      roleType: (section.role_type as TRoleFilter) ?? "",
      order: String(section.order ?? 0),
      isActive: section.is_active ?? true,
    });

  const handleSave = async () => {
    if (!editor.name.trim()) {
      toast({ variant: "destructive", title: "Name is required" });
      return;
    }
    setIsSaving(true);
    const orderNum = Number(editor.order);
    const roleValue = editor.roleType === "" ? null : editor.roleType;

    if (editor.section) {
      const payload: TUpdateSectionRequest = {
        name: editor.name.trim(),
        description: editor.description.trim() || null,
        role_type: roleValue,
        order: Number.isFinite(orderNum) ? orderNum : 0,
        is_active: editor.isActive,
      };
      const { error: err } = await adminService.updateSection(editor.section.id, payload);
      if (err) {
        toast({
          variant: "destructive",
          title: "Failed to update section",
          description: await extractErrorMessage(err),
        });
      } else {
        toast({ title: "Section updated" });
        setEditor(EMPTY_EDITOR);
        reload();
      }
    } else {
      const payload: TCreateSectionRequest = {
        name: editor.name.trim(),
        description: editor.description.trim() || null,
        role_type: roleValue,
        order: Number.isFinite(orderNum) ? orderNum : 0,
      };
      const { error: err } = await adminService.createSection(payload);
      if (err) {
        toast({
          variant: "destructive",
          title: "Failed to create section",
          description: await extractErrorMessage(err),
        });
      } else {
        toast({ title: "Section created" });
        setEditor(EMPTY_EDITOR);
        reload();
      }
    }
    setIsSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    const { error: err } = await adminService.deleteSection(deleteTarget.id);
    if (err) {
      toast({
        variant: "destructive",
        title: "Failed to delete section",
        description: await extractErrorMessage(err),
      });
    } else {
      toast({ title: "Section deleted" });
      setDeleteTarget(null);
      reload();
    }
    setIsDeleting(false);
  };

  const roleTabs: { label: string; value: TRoleFilter }[] = [
    { label: "All", value: "" },
    { label: "Fundraiser", value: "FUNDRAISER" },
    { label: "Investor", value: "INVESTOR" },
  ];

  const columns: TColumnDef<TAdminSection>[] = [
    {
      id: "name",
      header: "Name",
      sortable: true,
      accessor: (s) => s.name ?? "",
      cell: (s) => (
        <div>
          <div className="font-medium text-foreground">{s.name}</div>
          {s.description && <div className="text-xs text-muted-foreground">{s.description}</div>}
        </div>
      ),
    },
    {
      id: "role_type",
      header: "Role",
      sortable: true,
      accessor: (s) => s.role_type ?? "All roles",
    },
    { id: "order", header: "Order", sortable: true, accessor: (s) => s.order ?? 0 },
    {
      id: "is_active",
      header: "Status",
      sortable: true,
      accessor: (s) => (s.is_active ? 1 : 0),
      cell: (s) => (
        <Badge variant={s.is_active ? "secondary" : "outline"}>{s.is_active ? "Active" : "Inactive"}</Badge>
      ),
    },
  ];

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Sections</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Manage global sections available to companies
            </p>
          </div>
        </div>

        {/* Role filter (client-side; drives the getSections role param) */}
        <div className="flex flex-wrap gap-2">
          {roleTabs.map((tab) => (
            <Button
              key={tab.value || "all"}
              variant={roleFilter === tab.value ? "default" : "outline"}
              size="sm"
              onClick={() => setRoleFilter(tab.value)}
            >
              {tab.label}
            </Button>
          ))}
        </div>

        <DataGrid<TAdminSection>
          getRowId={(row) => row.id}
          persistKey="admin.sections"
          ariaLabel="Sections"
          data={{
            mode: "client",
            fetcher,
            deps: [roleFilter, reloadTick],
            searchFields: ["name", "description"],
          }}
          columns={columns}
          pagination={{ mode: "offset", pageSize: PAGE_SIZE, pageSizeOptions: [10, 20, 50, 100] }}
          toolbar={{
            search: true,
            refresh: true,
            create: { label: "New Section", onClick: openCreate, icon: <Plus className="h-4 w-4" /> },
            columnVisibility: true,
            density: true,
            export: ["csv", "json"],
            searchPlaceholder: "Search by name or description",
          }}
          rowActions={(s) => [
            { label: "Edit", icon: <Pencil className="h-4 w-4" />, onClick: () => openEdit(s) },
            {
              label: "Delete",
              icon: <Trash2 className="h-4 w-4" />,
              variant: "destructive",
              onClick: () => setDeleteTarget(s),
            },
          ]}
          emptyState={{ title: "No sections found." }}
        />
      </div>

      {/* Create/Edit dialog */}
      <Dialog
        open={editor.open}
        onOpenChange={(open) => {
          if (!open) setEditor(EMPTY_EDITOR);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editor.section ? "Edit Section" : "New Section"}</DialogTitle>
            <DialogDescription>
              Sections group content shown to companies during onboarding.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Name</label>
              <Input
                value={editor.name}
                onChange={(e) => setEditor((s) => ({ ...s, name: e.target.value }))}
                placeholder="Section name"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Description
              </label>
              <Textarea
                value={editor.description}
                onChange={(e) => setEditor((s) => ({ ...s, description: e.target.value }))}
                placeholder="Optional description"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  Role type
                </label>
                <select
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={editor.roleType}
                  onChange={(e) =>
                    setEditor((s) => ({ ...s, roleType: e.target.value as TRoleFilter }))
                  }
                >
                  <option value="">All roles</option>
                  <option value="FUNDRAISER">Fundraiser</option>
                  <option value="INVESTOR">Investor</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Order</label>
                <Input
                  type="number"
                  min={0}
                  value={editor.order}
                  onChange={(e) => setEditor((s) => ({ ...s, order: e.target.value }))}
                />
              </div>
            </div>
            {editor.section && (
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={editor.isActive}
                  onChange={(e) => setEditor((s) => ({ ...s, isActive: e.target.checked }))}
                />
                Active
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditor(EMPTY_EDITOR)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={() => void handleSave()} disabled={isSaving}>
              {isSaving ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Section</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete “{deleteTarget?.name}”? This deactivates the section.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => void handleDelete()} disabled={isDeleting}>
              {isDeleting ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting…</>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
